import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMarketingTemplatesRequest,
  fetchMarketingTemplatesSuccess,
  fetchMarketingTemplatesFailure,
  searchMarketingTemplatesRequest,
  searchMarketingTemplatesSuccess,
  searchMarketingTemplatesFailure,
  fetchMarketingTemplateDetailsRequest,
  fetchMarketingTemplateDetailsSuccess,
  fetchMarketingTemplateDetailsFailure,
  createMarketingTemplateRequest,
  createMarketingTemplateSuccess,
  createMarketingTemplateFailure,
  updateMarketingTemplateRequest,
  updateMarketingTemplateSuccess,
  updateMarketingTemplateFailure,
  deleteMarketingTemplateRequest,
  deleteMarketingTemplateSuccess,
  deleteMarketingTemplateFailure,
} from '../slices/marketingTemplate.slice';

import {
  getMarketingTemplates,
  getMarketingTemplateById,
  createMarketingTemplate,
  updateMarketingTemplate,
  deleteMarketingTemplate,
  searchMarketingTemplates,
} from '@/services/marketingService';

import { 
  CreateMarketingTemplateDto,
  UpdateMarketingTemplateDto,
  FetchMarketingTemplatesOptions 
} from '@/types/models/marketing';
import { toApiError } from '@/lib/errorHelpers';

function* fetchMarketingTemplatesSaga(action: PayloadAction<FetchMarketingTemplatesOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(getMarketingTemplates, action.payload);
    yield put(fetchMarketingTemplatesSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: action.payload.page || 1,
        total: response.length || 0,
        pageSize: action.payload.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (action.payload.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(fetchMarketingTemplatesFailure(toApiError(error)));
  }
}

function* searchMarketingTemplatesSaga(action: PayloadAction<{ query: string; options?: FetchMarketingTemplatesOptions }>): Generator<any, void, any> {
  try {
    const { query, options = {} } = action.payload;
    const response: any = yield call(searchMarketingTemplates, { query, ...options });
    yield put(searchMarketingTemplatesSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: options.page || 1,
        total: response.length || 0,
        pageSize: options.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (options.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(searchMarketingTemplatesFailure(toApiError(error)));
  }
}

function* fetchMarketingTemplateDetailsSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    const response: any = yield call(getMarketingTemplateById, action.payload.id);
    yield put(fetchMarketingTemplateDetailsSuccess(response));
  } catch (error: any) {
    yield put(fetchMarketingTemplateDetailsFailure(toApiError(error)));
  }
}

function* createMarketingTemplateSaga(action: PayloadAction<CreateMarketingTemplateDto>): Generator<any, void, any> {
  try {
  const response: any = yield call(createMarketingTemplate as any, action.payload);
    yield put(createMarketingTemplateSuccess(response));
  } catch (error: any) {
  yield put(createMarketingTemplateFailure(toApiError(error)));
  }
}

function* updateMarketingTemplateSaga(action: PayloadAction<{ id: string; data: UpdateMarketingTemplateDto }>): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
  const response: any = yield call(updateMarketingTemplate as any, id, data);
    yield put(updateMarketingTemplateSuccess(response));
  } catch (error: any) {
  yield put(updateMarketingTemplateFailure(toApiError(error)));
  }
}

function* deleteMarketingTemplateSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    yield call(deleteMarketingTemplate, action.payload.id);
    yield put(deleteMarketingTemplateSuccess({ id: action.payload.id }));
  } catch (error: any) {
  yield put(deleteMarketingTemplateFailure(toApiError(error)));
  }
}

export function* marketingTemplateSaga(): Generator<any, void, any> {
  yield takeLatest(fetchMarketingTemplatesRequest.type, fetchMarketingTemplatesSaga);
  yield takeLatest(searchMarketingTemplatesRequest.type, searchMarketingTemplatesSaga);
  yield takeLatest(fetchMarketingTemplateDetailsRequest.type, fetchMarketingTemplateDetailsSaga);
  yield takeLatest(createMarketingTemplateRequest.type, createMarketingTemplateSaga);
  yield takeLatest(updateMarketingTemplateRequest.type, updateMarketingTemplateSaga);
  yield takeLatest(deleteMarketingTemplateRequest.type, deleteMarketingTemplateSaga);
}

export default marketingTemplateSaga;